import { useCallback } from "react";
import { useApolloClient } from "@apollo/client/react";
import { IssueByIdDocument, type IssueDetail } from "#app/lib/data";

// Shared by this imperative hook and the declarative useIssueById so both read
// the same document/policy and warm the same cache entry — keep them in sync.
export const ISSUE_BY_ID_DOCUMENT = IssueByIdDocument;
export const ISSUE_BY_ID_FETCH_POLICY = "cache-first" as const;

export function toIssueSummary(
  data: { issue?: unknown } | null | undefined,
): IssueDetail | null {
  return (data?.issue as IssueDetail | null) ?? null;
}

/**
 * Returns a function that fetches an issue by ID from the Apollo cache (or
 * network). Extracted so that context providers don't import Apollo directly.
 *
 * Resolves to the issue, or `null` when the query SUCCEEDS but the issue isn't
 * visible to the viewer (private/no-share) or was deleted. It REJECTS on a real
 * fetch failure (network/server error) — callers must distinguish the two: a
 * clean null means "no access", a rejection means "couldn't load". Conflating
 * them would tell a user they lack access when the request merely failed.
 */
export function useFetchIssueById() {
  const apolloClient = useApolloClient();

  return useCallback(
    async (issueId: string): Promise<IssueDetail | null> => {
      // NOTE: deliberately no try/catch. A real fetch failure must REJECT so
      // callers can tell "no access" (clean null) from "couldn't load" (throw)
      // — see the contract in this function's docstring and openIssueById.
      const { data } = await apolloClient.query({
        query: ISSUE_BY_ID_DOCUMENT,
        variables: { issueId },
        fetchPolicy: ISSUE_BY_ID_FETCH_POLICY,
      });
      return toIssueSummary(data);
    },
    [apolloClient],
  );
}
